import { useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDate } from '../../lib/format'
import { parseNarrative, routeFor } from '../../lib/weekly'
import type { Citation, WeeklyView } from '../../types/weekly'

// The week, written: the model's prose over the facts above, each sentence ending in the keys it rests on; the
// keys are links to the fact. What it cites is listed beside it so the reader can check the prose against the
// record. Marked plainly when no model wrote it.
export function Narrative({ view, cites, onWrite, writing }: { view: WeeklyView; cites: Map<string, Citation>; onWrite: () => void; writing: boolean }) {
  const n = view.narrative
  const sections = n ? parseNarrative(n.text) : []
  return (
    <div className="card narrative-card">
      <div className="card-head">
        <div>
          <h2>The week, written</h2>
          <p className="muted">
            {n
              ? n.modelWritten
                ? `Written ${formatDate(n.writtenAt)}${n.modelId ? ` by ${n.modelId}` : ''}, citing ${n.citations.length} fact${n.citations.length === 1 ? '' : 's'} on file. Every bracketed key is a link to the fact it rests on.`
                : `Laid out ${formatDate(n.writtenAt)} from the facts on file without a model: plain sentences, one per fact, not prose.`
              : 'Not written yet. Writing reads the facts above and produces the update, every sentence citing the fact it rests on.'}
          </p>
        </div>
        {n && !n.modelWritten && <span className="tag">not model-written</span>}
      </div>

      {!view.modelConfigured && (
        <div className="refusal">
          <strong>No model connected</strong>
          <p>Writing the week needs a model. Until one is configured for this instance, writing lays the facts out in plain sentences and marks the result as not model-written.</p>
        </div>
      )}

      {!n && (
        <div className="narrative-empty">
          <p className="muted">The movement above is measured and ready to be written.</p>
          {!view.activeJobId && <button className="button primary" onClick={onWrite} disabled={writing}>{writing ? 'Starting…' : 'Write this week'}</button>}
          {view.activeJobId && <Link className="button" to={`/jobs/${encodeURIComponent(view.activeJobId)}`}>Being written now: watch the job</Link>}
        </div>
      )}

      {n && (
        <div className="narrative-grid">
          <div className="narrative-prose">
            {sections.map((s, i) => (
              <section key={i} className="narrative-section">
                {s.title && <h3>{s.title}</h3>}
                {s.paragraphs.map((p, j) => (
                  <p key={j}>
                    {p.map((seg, k) => seg.type === 'text'
                      ? <span key={k}>{seg.text}</span>
                      : <CiteGroup key={k} keys={seg.keys} cites={cites} />)}
                  </p>
                ))}
              </section>
            ))}
          </div>
          <aside className="narrative-cited">
            <p className="kicker">What it cites</p>
            {n.citations.length === 0 && <p className="muted small">No fact was cited.</p>}
            <ul className="plain">
              {n.citations.map((c) => (
                <li key={c.key}>
                  <span className="fact-key cited">{c.key}</span>
                  <Link to={routeFor(c.kind, c.id)}>{c.label}</Link>
                  <span className="muted small">{c.kind}</span>
                </li>
              ))}
            </ul>
            {view.citations.length > n.citations.length && <p className="muted small">{view.citations.length - n.citations.length} fact{view.citations.length - n.citations.length === 1 ? '' : 's'} on file went uncited.</p>}
          </aside>
        </div>
      )}
    </div>
  )
}

// A sentence over twenty-seven people cites twenty-seven keys; six show, the rest open on a click.
const SHOW = 6
function CiteGroup({ keys, cites }: { keys: string[]; cites: Map<string, Citation> }) {
  const [open, setOpen] = useState(false)
  const shown = open ? keys : keys.slice(0, SHOW)
  return (
    <span className="cites">
      {shown.map((key) => <Cite key={key} k={key} c={cites.get(key)} />)}
      {keys.length > SHOW && !open && <button className="fact-key more" onClick={() => setOpen(true)} title={keys.slice(SHOW).join(', ')}>+{keys.length - SHOW}</button>}
    </span>
  )
}

function Cite({ k, c }: { k: string; c: Citation | undefined }) {
  if (!c) return <span className="fact-key unknown" title="Not on file this week">{k}</span>
  return <Link className="fact-key cited" to={routeFor(c.kind, c.id)} title={`${c.kind}: ${c.label}`}>{k}</Link>
}
